import { DEFAULT_LIMIT, DEFAULT_SKIP, LIMIT } from 'Src/consts/table';
import { Count } from 'Src/models/count';
import { superAdminDelete, superAdminGet, superAdminPost, superAdminPut } from './api';

export interface SuperAdminSportsFilter {
  limit?: number;
  skip?: number;
  order?: string;
  where?: any;
}

export interface SuperAdminSport {
  id: string;
  _id?: string;
  name?: string;
  description?: string;
  [key: string]: any;
}

export interface SuperAdminSportUpdateData {
  name?: string;
  description?: string;
  scoring?: string;
  players?: string;
  multiparty?: boolean;
  individualResultsAvailable?: boolean;
  isSchoolEventsAvailable?: boolean;
  isClubEventsAvailable?: boolean;
  [key: string]: any;
}

const DEFAULT_FILTER: SuperAdminSportsFilter = {
  limit: LIMIT,
  skip: DEFAULT_SKIP,
  order: 'name ASC'
};

function buildFilter(filter: SuperAdminSportsFilter = DEFAULT_FILTER): SuperAdminSportsFilter {
  return {
    where: filter.where || {},
    skip: typeof filter.skip === 'number' ? filter.skip : DEFAULT_FILTER.skip,
    limit: typeof filter.limit === 'number' ? filter.limit : DEFAULT_FILTER.limit,
    order: filter.order || DEFAULT_FILTER.order
  };
}

export function getSuperAdminSports(filter: SuperAdminSportsFilter = DEFAULT_FILTER): Promise<SuperAdminSport[]> {
  return superAdminGet<SuperAdminSport[]>('/sports', {
    params: {
      filter: buildFilter(filter)
    }
  });
}

export function createSuperAdminSport(data: SuperAdminSportUpdateData): Promise<SuperAdminSport> {
  return superAdminPost<SuperAdminSport>('/sports', data);
}

export function updateSuperAdminSport(sportId: string, data: SuperAdminSportUpdateData): Promise<SuperAdminSport> {
  return superAdminPut<SuperAdminSport>(`/sports/${sportId}`, data);
}

export function deleteSuperAdminSport(sportId: string): Promise<void> {
  return superAdminDelete<void>(`/sports/${sportId}`);
}

export function getAllSuperAdminSports(where?: any): Promise<SuperAdminSport[]> {
  return superAdminGet<SuperAdminSport[]>('/sports', {
    params: {
      filter: {
        where: where || {},
        skip: DEFAULT_SKIP,
        limit: DEFAULT_LIMIT,
        order: DEFAULT_FILTER.order
      }
    }
  });
}

export function getSuperAdminSportsCount(where?: any): Promise<Count> {
  const config =
    typeof where !== 'undefined'
      ? {
          params: {
            filter: {
              where
            }
          }
        }
      : {};

  return superAdminGet<Count>('/sports/count', config);
}

export function getSuperAdminSport(sportId: string): Promise<SuperAdminSport> {
  return superAdminGet<SuperAdminSport>(`/sports/${sportId}`);
}

export function getSuperAdminSchoolSports(
  schoolId: string,
  filter: SuperAdminSportsFilter = DEFAULT_FILTER
): Promise<SuperAdminSport[]> {
  return superAdminGet<SuperAdminSport[]>(`/schools/${schoolId}/sports`, {
    params: {
      filter: buildFilter(filter)
    }
  });
}

export function getAllSuperAdminSchoolSports(schoolId: string, where?: any): Promise<SuperAdminSport[]> {
  return superAdminGet<SuperAdminSport[]>(`/schools/${schoolId}/sports`, {
    params: {
      filter: {
        where: where || {},
        skip: DEFAULT_SKIP,
        limit: DEFAULT_LIMIT,
        order: DEFAULT_FILTER.order
      }
    }
  });
}

export function getSuperAdminSchoolSportsCount(schoolId: string, where?: any): Promise<Count> {
  const config =
    typeof where !== 'undefined'
      ? {
          params: {
            filter: {
              where
            }
          }
        }
      : {};

  return superAdminGet<Count>(`/schools/${schoolId}/sports/count`, config);
}

export function getSuperAdminSchoolSport(schoolId: string, sportId: string): Promise<SuperAdminSport> {
  return superAdminGet<SuperAdminSport>(`/schools/${schoolId}/sports/${sportId}`);
}

export function setSchoolSportFavorite(
  schoolId: string,
  sportId: string,
  isFavorite: boolean
): Promise<SuperAdminSport> {
  return superAdminPut<SuperAdminSport>(`/schools/${schoolId}/sports/${sportId}`, { isFavorite });
}
